import React, { useCallback, useEffect, useRef, useState } from 'react'
import { vn } from 'vend-number'

import { debounce } from 'lodash'
import { T } from '@transifex/react'
import { LineItem } from '../../../models/line-item'
import { displayRound } from '../../../filters/display-round'
import {
  useSaleManager,
  useUserHasLineItemDiscountRule,
  useVdFeatureManager,
  useVdTracking,
} from '../../../ng-compat/hooks'
import { $rootScope } from '../../../ng-compat/imports'
import { ERRORS, FEATURE_ENABLE_CONTROL_ON_DISCOUNT } from '../../../constants'

import { LineItemInput } from './LineItemInput'
import { useLineItemDiscountRule } from './useLineItemDiscountRule'

interface Props {
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  onError: (errors: string | null) => void
  error: string | null
  hasUserSwitched: () => boolean
  readonly?: boolean
}

export const LineItemDiscountPercent: React.FC<Props> = ({
  lineItem,
  editAllowedForConfirmed = false,
  onError,
  error: initialError,
  hasUserSwitched,
  readonly = false,
}) => {
  const vdTracking = useVdTracking()
  const discountPermission = useUserHasLineItemDiscountRule()
  const canApplyDiscount = discountPermission && !vn(lineItem.basePrice).eq(0)

  const [error, setError] = useState<string | null>(initialError)
  const [value, setValue] = useState<string | number>(
    lineItem.displayDiscountPercent
  )
  const isReadOnlyField =
    readonly ||
    !canApplyDiscount ||
    (lineItem.confirmed && !editAllowedForConfirmed) ||
    lineItem.isReturn
  const [disabled, setDisabled] = useState<boolean>(isReadOnlyField)

  const setErrorState = (error: string | null) => {
    setError(error)
    onError(error)
  }

  const setDisabledState = (disabled: boolean) => {
    // is the field currently enabled or it's being disabled
    const canChangeDisabledState = !isReadOnlyField || disabled
    if (canChangeDisabledState) {
      setDisabled(disabled)
    }
  }

  useEffect(() => {
    if (error !== ERRORS.INVALID_DISCOUNT) {
      setValue(lineItem.displayDiscountPercent)
    }
  }, [lineItem, error])

  const vdFeatureManager = useVdFeatureManager()
  const hasControlOnDiscountEnabled = vdFeatureManager.get(
    FEATURE_ENABLE_CONTROL_ON_DISCOUNT
  )
  const saleManager = useSaleManager()

  const {
    isPercentDiscountValid,
    lineItemDiscountRule,
  } = useLineItemDiscountRule(lineItem)

  // validate the discount set based on user line item discount rules
  const validateDiscount = useCallback(
    (config: { value: string | number; ignoreAdjusted?: boolean }) => {
      if (
        (lineItem.adjusted || config.ignoreAdjusted) &&
        hasControlOnDiscountEnabled &&
        !isPercentDiscountValid(config.value)
      ) {
        return ERRORS.INVALID_DISCOUNT
      }

      if (vn(config.value).lt(0)) {
        return ERRORS.NEGATIVE_DISCOUNT
      }

      return null
    },
    [hasControlOnDiscountEnabled, isPercentDiscountValid, lineItem.adjusted]
  )

  // re-render whenever the error changes on the angular side
  useEffect(() => {
    setError(initialError)
  }, [initialError])

  useEffect(() => {
    const error = Boolean(validateDiscount({ value }))
    setDisabledState(error)
    // on the first render, set field disabled if current user is not allowed to have/modify the current discount value
    // since we do not persist line items with invalid discount this would've been set by a user with higher privileges
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Angular periodically recreates the component and calls loggedInUserChange
    // this useEffect should only run if the user really switched
    if (hasUserSwitched()) {
      const discountValue =
        error === ERRORS.INVALID_DISCOUNT
          ? lineItem.displayDiscountPercent
          : value

      setValue(discountValue)

      const newLoggedInUserError = validateDiscount({
        value: discountValue,
      })
      const isInvalidDiscountError =
        newLoggedInUserError === ERRORS.INVALID_DISCOUNT

      setDisabledState(isReadOnlyField || isInvalidDiscountError)

      if (error === ERRORS.INVALID_DISCOUNT) {
        setErrorState(null)
      }
    }
    // when userRule changes (when switching users)
    // set the previous valid discount if the current value was invalid before switching users
    // and/or make the field read-only + clear errors if the new logged in user
    // is not allowed to have/modify the current discount value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemDiscountRule])

  // TODO: [LR-10986] move this inside onChange
  const sendUbe = useRef(
    debounce((inputValue: string, err: string | null) => {
      const eventName = err
        ? 'sale.line-item.discount-percent-changed.not_allowed_error'
        : 'sale.line-item.discount-percent-changed'
      vdTracking.trackUserEvent({
        eventName,
        metadata: {
          valueSet: inputValue,
        },
      })
    }, 300)
  )

  // TODO: [LR-10986] add debounce
  const onChange = (inputValue: string) => {
    const error = validateDiscount({ value: inputValue, ignoreAdjusted: true })

    setErrorState(error)
    setValue(inputValue)

    if (
      error !== ERRORS.INVALID_DISCOUNT &&
      error !== ERRORS.NEGATIVE_DISCOUNT
    ) {
      saleManager.updateLineItemDiscountPercent({
        lineItem: {
          ...lineItem,
          displayDiscountPercent: inputValue,
        },
      })
    }

    sendUbe.current(inputValue, error)
  }

  const onBlur = useCallback(
    (_: string) => {
      $rootScope.$emit('sale:update-line-item', lineItem)
    },
    [lineItem]
  )

  return (
    <div>
      <div className="vd-field li-cell-actions-field">
        <div className="vd-label">
          <T _str="Discount" />
        </div>
        <div className="vd-value li-cell-actions-value">
          {disabled ? (
            <div>{displayRound(lineItem.displayDiscountPercent)}%</div>
          ) : (
            <>
              <LineItemInput
                className="li-input--discount-percent wr-sale-discount-input vd-input--icon-right"
                allowedSuffix="%"
                min={0}
                error={error}
                onError={setErrorState}
                onValueChange={onChange}
                onFocusChange={onBlur}
                value={value}
                trimInsignificantDigits
              />
              <div className="wr-sale-discount-input-symbol vd-input-icon vd-input-icon--right">
                %
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
